// CustomerProfileEdit.js
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  User,
  Phone,
  Mail,
  MapPin,
  Plus,
  Trash2,
  CheckCircle2,
  Circle,
  Globe,
  Building2,
  Building,
  Layers,
  Home,
  FileText,
  ChevronDown,
} from "lucide-react";
import { Button } from "../ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { auth, db, storage } from "../../firebase";

import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

import MapPicker from "../maps/MapPicker";
import useUserGeolocation from "../../hooks/useUserGeolocation";

// ✅ NEW
import { reverseGeocode } from "../../lib/geocoding";

const SAUDI_CITIES = [
  "Abha",
  "Al-Ahsa",
  "Al Bahah",
  "Al-Kharj",
  "Al-Qassim",
  "Arar",
  "Buraydah",
  "Dammam",
  "Dhahran",
  "Hail",
  "Hafar Al-Batin",
  "Jazan",
  "Jeddah",
  "Jubail",
  "Khamis Mushait",
  "Khobar",
  "Mecca",
  "Medina",
  "Najran",
  "Qatif",
  "Riyadh",
  "Sakakah",
  "Tabuk",
  "Taif",
  "Yanbu",
];

const stripSaudiPrefix = (val) => {
  const s = (val ?? "").toString().trim();
  return s
    .replace(/^(\+?966|00966)\s?/, "")
    .replace(/\s/g, "")
    .replace(/[^\d]/g, "");
};

const formatSaudiPhone = (digits) => {
  const d = (digits ?? "").toString().replace(/[^\d]/g, "");
  if (!d) return "";
  return `+966 ${d}`.trim();
};

const isValidLatLng = (loc) => {
  const lat = loc?.lat;
  const lng = loc?.lng;
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  );
};

export default function CustomerProfileEdit() {
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  const uid = profile?.uid || auth?.currentUser?.uid || null;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    imageUrl: "",
    country: "Saudi Arabia",
    city: "",
  });

  const [phoneNumber, setPhoneNumber] = useState("");

  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);

  const initialNewAddress = useMemo(
    () => ({
      type: "Home",
      addressText: "",
      phone: "",
      buildingName: "",
      buildingNo: "",
      floorNo: "",
      aptNo: "",
      instructions: "",
      location: { lat: null, lng: null },
    }),
    []
  );

  const [newAddress, setNewAddress] = useState(initialNewAddress);

  // show geocoder issues (don’t swallow)
  const [geoErrorMsg, setGeoErrorMsg] = useState("");

  // used to prevent racing updates while dragging
  const geocodeReqIdRef = useRef(0);

  const geo = useUserGeolocation({ enabled: showAddAddress });

  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ---------- Seed form from useAuth profile ----------
  useEffect(() => {
    if (!profile) return;

    const rp = profile.roleProfile || {};
    const name = (rp.name ?? profile.name ?? "").toString();
    const email = (rp.email ?? profile.email ?? auth?.currentUser?.email ?? "").toString();
    const phone = (rp.phone ?? "").toString();
    const imageUrl = (rp.imageUrl ?? rp.photoURL ?? "").toString();
    const country = (rp.country ?? "Saudi Arabia").toString() || "Saudi Arabia";
    const city = (rp.city ?? "").toString();

    const digits = stripSaudiPrefix(phone);

    setForm({
      name,
      email,
      phone: phone ? phone : "",
      imageUrl,
      country,
      city,
    });

    setPhoneNumber(digits);
  }, [profile]);

  // ---------- Subscribe to addresses ----------
  useEffect(() => {
    if (!uid) {
      setAddresses([]);
      setAddressesLoading(false);
      return;
    }

    setAddressesLoading(true);
    const colRef = collection(db, "customers", uid, "savedAddresses");

    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          const loc = data.location && typeof data.location === "object" ? data.location : {};
          return {
            id: d.id,
            type: typeof data.type === "string" ? data.type : "Other",
            addressText: typeof data.addressText === "string" ? data.addressText : "",
            phone: typeof data.phone === "string" ? data.phone : "",
            buildingName: typeof data.buildingName === "string" ? data.buildingName : "",
            buildingNo: typeof data.buildingNo === "string" ? data.buildingNo : "",
            floorNo: typeof data.floorNo === "string" ? data.floorNo : "",
            aptNo: typeof data.aptNo === "string" ? data.aptNo : "",
            instructions: typeof data.instructions === "string" ? data.instructions : "",
            isDefault: !!data.isDefault,
            location: { lat: loc.lat ?? null, lng: loc.lng ?? null },
          };
        });

        list.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
        setAddresses(list);
        setAddressesLoading(false);
      },
      (err) => {
        console.error("addresses snapshot error:", err);
        setAddresses([]);
        setAddressesLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  // ---------- Personal phone input ----------
  const handlePhoneNumberChange = (e) => {
    const newDigits = (e.target.value ?? "").toString();
    if (!/^\d*$/.test(newDigits)) return;

    setPhoneNumber(newDigits);
    setForm((prev) => ({ ...prev, phone: formatSaudiPhone(newDigits) }));
  };

  // ---------- Address phone input ----------
  const handleNewAddressPhoneDigitsChange = (e) => {
    const digits = (e.target.value ?? "").toString();
    if (!/^\d*$/.test(digits)) return;

    setNewAddress((prev) => ({ ...prev, phone: formatSaudiPhone(digits) }));
  };

  const newAddressPhoneDigits = useMemo(
    () => stripSaudiPrefix(newAddress.phone),
    [newAddress.phone]
  );

  // ---------- Reverse Geocode using shared util ----------
  const reverseGeocodeAndSetAddressText = async (coords) => {
    if (!isValidLatLng(coords)) return;

    const reqId = ++geocodeReqIdRef.current;
    setGeoErrorMsg("");

    try {
      const { formattedAddress } = await reverseGeocode(coords, {
        cacheTtlMs: 5 * 60 * 1000,
        cachePrecision: 6,
      });

      // ignore stale result
      if (reqId !== geocodeReqIdRef.current) return;

      setNewAddress((prev) => ({ ...prev, addressText: formattedAddress }));
    } catch (e) {
      if (reqId !== geocodeReqIdRef.current) return;
      console.warn("Reverse geocode failed:", e);
      setGeoErrorMsg(
        "Geocoder failed. Check: Billing enabled + Geocoding API enabled + API key restrictions."
      );
    }
  };

  /**
   * ✅ Requirements when Add New opens:
   * - Show map immediately
   * - Pin current location (if available)
   * - Delivering to is AUTO (no typing)
   * - Address phone auto-filled from personal phone (editable)
   */
  useEffect(() => {
    if (!showAddAddress) return;

    setGeoErrorMsg("");

    // Auto-fill address phone (only if empty)
    setNewAddress((prev) => {
      const hasAddrPhone = (prev.phone || "").trim().length > 0;
      const personalPhone = (form.phone || "").trim();
      if (!hasAddrPhone && personalPhone) return { ...prev, phone: personalPhone };
      return prev;
    });

    // Auto-pin current location (only if not set yet)
    const userCoords = geo?.coords || null;
    if (!isValidLatLng(userCoords)) return;

    const hasLocation = isValidLatLng(newAddress.location);
    if (!hasLocation) {
      const coords = { lat: userCoords.lat, lng: userCoords.lng };
      setNewAddress((prev) => ({ ...prev, location: coords }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddAddress, geo?.coords?.lat, geo?.coords?.lng, form.phone]);

  /**
   * ✅ Always update addressText whenever the pin location changes
   * - This is what makes it dynamic according to the pin
   */
  useEffect(() => {
    if (!showAddAddress) return;
    const loc = newAddress.location;
    if (!isValidLatLng(loc)) return;

    const t = setTimeout(() => {
      reverseGeocodeAndSetAddressText({ lat: loc.lat, lng: loc.lng });
    }, 250); // debounce while dragging

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddAddress, newAddress.location?.lat, newAddress.location?.lng]);

  // ---------- Profile Save (Firestore) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uid) return;

    try {
      setSaving(true);

      const customerRef = doc(db, "customers", uid);

      await updateDoc(customerRef, {
        name: (form.name || "").trim(),
        email: (form.email || "").trim(),
        phone: (form.phone || "").trim(),
        imageUrl: (form.imageUrl || "").trim(),
        country: (form.country || "Saudi Arabia").trim() || "Saudi Arabia",
        city: (form.city || "").trim(),
        updatedAt: serverTimestamp(),
      });

      navigate("/customer/home");
    } catch (err) {
      console.error("Save profile error:", err);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Address CRUD ----------
  const handleAddAddress = async () => {
    if (!uid) return;

    const addressText = (newAddress.addressText || "").trim();
    if (!addressText) return;

    try {
      const batch = writeBatch(db);
      const colRef = collection(db, "customers", uid, "savedAddresses");
      const willBeDefault = addresses.length === 0;

      if (willBeDefault) {
        addresses.forEach((a) => {
          if (a.isDefault) {
            batch.update(doc(db, "customers", uid, "savedAddresses", a.id), {
              isDefault: false,
              updatedAt: serverTimestamp(),
            });
          }
        });
      }

      const newRef = doc(colRef);

      batch.set(
        newRef,
        {
          type: (newAddress.type || "Other").toString(),
          addressText,
          phone: (newAddress.phone || "").toString().trim(),
          buildingName: (newAddress.buildingName || "").toString().trim(),
          buildingNo: (newAddress.buildingNo || "").toString().trim(),
          floorNo: (newAddress.floorNo || "").toString().trim(),
          aptNo: (newAddress.aptNo || "").toString().trim(),
          instructions: (newAddress.instructions || "").toString().trim(),
          location: {
            lat: newAddress.location?.lat ?? null,
            lng: newAddress.location?.lng ?? null,
          },
          isDefault: willBeDefault,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();

      setNewAddress(initialNewAddress);
      setShowAddAddress(false);
    } catch (err) {
      console.error("Add address error:", err);
    }
  };

  const deleteAddress = async (addressId) => {
    if (!uid || !addressId) return;

    try {
      const batch = writeBatch(db);

      const target = addresses.find((a) => a.id === addressId);
      const isDefault = !!target?.isDefault;

      batch.delete(doc(db, "customers", uid, "savedAddresses", addressId));

      if (isDefault) {
        const next = addresses.find((a) => a.id !== addressId);
        if (next) {
          batch.update(doc(db, "customers", uid, "savedAddresses", next.id), {
            isDefault: true,
            updatedAt: serverTimestamp(),
          });
        }
      }

      await batch.commit();
    } catch (err) {
      console.error("Delete address error:", err);
    }
  };

  const setDefaultAddress = async (addressId) => {
    if (!uid || !addressId) return;

    try {
      const batch = writeBatch(db);

      addresses.forEach((a) => {
        const ref = doc(db, "customers", uid, "savedAddresses", a.id);
        batch.update(ref, {
          isDefault: a.id === addressId,
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();
    } catch (err) {
      console.error("Set default address error:", err);
    }
  };

  // ---------- Photo Upload (Firebase Storage) ----------
  const openFilePicker = () => {
    if (!uid || uploadingPhoto) return;
    fileInputRef.current?.click();
  };

  const uploadCustomerAvatar = async (file) => {
    if (!uid || !file) return;

    const isImage = file.type?.startsWith("image/");
    const maxBytes = 5 * 1024 * 1024;
    if (!isImage) {
      alert("Please select an image file (JPG/PNG).");
      return;
    }
    if (file.size > maxBytes) {
      alert("Max file size is 5MB.");
      return;
    }

    setUploadingPhoto(true);
    setUploadProgress(0);

    try {
      const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
      const path = `profiles/customers/${uid}/avatar.${ext}`;
      const sRef = storageRef(storage, path);

      const task = uploadBytesResumable(sRef, file, {
        contentType: file.type || "image/jpeg",
      });

      const downloadURL = await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setUploadProgress(Number.isFinite(pct) ? pct : 0);
          },
          (err) => reject(err),
          async () => {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          }
        );
      });

      const customerRef = doc(db, "customers", uid);
      await updateDoc(customerRef, {
        imageUrl: downloadURL,
        updatedAt: serverTimestamp(),
      });

      setForm((prev) => ({ ...prev, imageUrl: downloadURL }));
    } catch (err) {
      console.error("Avatar upload error:", err);
      alert("Upload failed. Check Storage rules & authentication.");
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;
    await uploadCustomerAvatar(file);
  };

  const displayImage = useMemo(() => {
    return (
      form.imageUrl ||
      "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=150&h=150&fit=crop"
    );
  }, [form.imageUrl]);

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-600"
              type="button"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-[#1E3A8A]">Edit Profile</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Photo */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#1E3A8A] mb-6">
              Profile Photo
            </h2>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileSelected}
              className="hidden"
            />

            <div className="flex items-center gap-6">
              <div className="relative">
                <img
                  src={displayImage}
                  alt={form.name || "Customer"}
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                />

                <button
                  type="button"
                  className="absolute bottom-0 right-0 bg-[#006C35] text-white p-2 rounded-full hover:bg-[#005428] shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={openFilePicker}
                  disabled={!uid || uploadingPhoto}
                  title={!uid ? "Sign in first" : "Upload new photo"}
                >
                  <Camera size={16} />
                </button>
              </div>

              <div className="text-sm text-gray-500">
                <p>Allowed formats: JPG, PNG</p>
                <p>Max size: 5MB</p>

                {uploadingPhoto ? (
                  <div className="mt-3">
                    <div className="text-xs text-gray-600 mb-1">
                      Uploading... {uploadProgress}%
                    </div>
                    <div className="w-56 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#006C35]"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-xs">
                    Stored and saved in the storage as JPG, PNG. 
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#1E3A8A] mb-6">
              Personal Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <User
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 text-sm w-28 select-none">
                      <span className="mr-2">🇸🇦</span> +966
                    </div>
                    <div className="relative flex-1">
                      <Phone
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent"
                        placeholder="55 123 4567"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <div className="relative">
                    <Globe
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      value={form.country || "Saudi Arabia"}
                      disabled
                      readOnly
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <div className="relative">
                    <MapPin
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <select
                      value={form.city || ""}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006C35] focus:border-transparent appearance-none bg-white"
                    >
                      <option value="" disabled>
                        Select City
                      </option>
                      {SAUDI_CITIES.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      size={16}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#1E3A8A]">
                Saved Addresses
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddAddress((v) => !v);
                  if (!showAddAddress) setNewAddress(initialNewAddress);
                }}
                className="text-[#006C35] hover:text-[#005428] text-sm font-medium flex items-center gap-1"
              >
                <Plus size={16} />
                Add New
              </button>
            </div>

            {showAddAddress && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Edit Location
                </h3>

                <MapPicker
                  active={showAddAddress}
                  height={180}
                  userCenter={geo?.coords || null}
                  userZoom={17}
                  allowFullscreen={true}
                  enableMyLocationButton={true}
                  value={newAddress.location}
                  onChange={(coords) => {
                    setNewAddress((prev) => ({
                      ...prev,
                      location: { lat: coords.lat, lng: coords.lng },
                    }));
                    // reverse geocode happens via effect (dynamic)
                  }}
                />

                <div className="space-y-4 mt-4">
                  {/* Delivering to (NO INPUT) */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Delivering to
                    </label>

                    <div className="flex items-start gap-2 bg-white border border-gray-300 rounded-lg p-3">
                      <MapPin className="text-[#006C35] mt-0.5" size={18} />
                      <div className="flex-1">
                        <div className="text-sm text-gray-800">
                          {(newAddress.addressText || "").trim()
                            ? newAddress.addressText
                            : "Move the pin to select your delivery location."}
                        </div>

                        {geoErrorMsg ? (
                          <div className="text-xs text-red-600 mt-1">
                            {geoErrorMsg}
                          </div>
                        ) : isValidLatLng(newAddress.location) ? (
                          <div className="text-xs text-gray-500 mt-1">
                            Lat: {Number(newAddress.location.lat).toFixed(6)} | Lng:{" "}
                            {Number(newAddress.location.lng).toFixed(6)}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 mt-1">
                            Tip: Allow location permission to start from your current location.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address Label */}
                    <label className="block text-xs text-gray-500 mb-1 mt-3">
                      Address Label
                    </label>
                    <input
                      type="text"
                      value={newAddress.type}
                      onChange={(e) =>
                        setNewAddress({ ...newAddress, type: e.target.value })
                      }
                      className="w-full h-10 pl-10 pr-3 border border-gray-300 bg-white rounded-lg text-sm"
                      placeholder="e.g. Home, Work"
                    />
                  </div>

                  {/* Mobile Number (auto-filled from personal phone, editable) */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Mobile Number
                    </label>
                    <div className="flex gap-2">
                      <div className="flex items-center px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 text-sm w-24 select-none">
                        <span className="mr-2">🇸🇦</span> +966
                      </div>
                      <input
                        type="tel"
                        value={newAddressPhoneDigits}
                        onChange={handleNewAddressPhoneDigitsChange}
                        className="flex-1 px-3 py-2 border border-gray-300 bg-white rounded-lg text-sm"
                        placeholder="55 123 4567"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  {/* Building Details */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Building Name
                      </label>
                      <div className="relative">
                        <Building2
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          size={16}
                        />
                        <input
                          type="text"
                          value={newAddress.buildingName}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, buildingName: e.target.value })
                          }
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-white rounded-lg text-sm"
                          placeholder="e.g. Olaya View"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Building No.
                      </label>
                      <div className="relative">
                        <Building
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          size={16}
                        />
                        <input
                          type="text"
                          value={newAddress.buildingNo}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, buildingNo: e.target.value })
                          }
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-white rounded-lg text-sm"
                          placeholder="e.g. 12"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Floor & Apt */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Floor No.
                      </label>
                      <div className="relative">
                        <Layers
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          size={16}
                        />
                        <input
                          type="text"
                          value={newAddress.floorNo}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, floorNo: e.target.value })
                          }
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-white rounded-lg text-sm"
                          placeholder="Floor"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Apt No.
                      </label>
                      <div className="relative">
                        <Home
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          size={16}
                        />
                        <input
                          type="text"
                          value={newAddress.aptNo}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, aptNo: e.target.value })
                          }
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-white rounded-lg text-sm"
                          placeholder="Apt"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Additional Instructions
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 h-10 text-gray-400" size={16} />
                      <textarea
                        value={newAddress.instructions}
                        onChange={(e) =>
                          setNewAddress({ ...newAddress, instructions: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-white rounded-lg text-sm"
                        placeholder="e.g. Leave at door, near landmark..."
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddAddress(false);
                        setNewAddress(initialNewAddress);
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleAddAddress}
                      className="bg-[#006C35] hover:bg-[#005428] flex-1"
                      disabled={!uid || !(newAddress.addressText || "").trim()}
                    >
                      Save Address
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {addressesLoading ? (
                <div className="text-sm text-gray-500">Loading addresses...</div>
              ) : addresses.length === 0 ? (
                <div className="text-sm text-gray-500">No saved addresses yet.</div>
              ) : (
                addresses.map((addr) => {
                  const bldg = (addr.buildingName || "").trim();
                  const bno = (addr.buildingNo || "").trim();
                  const apt = (addr.aptNo || "").trim();

                  const buildingLine =
                    bldg || bno || apt
                      ? `Bldg: ${bldg || "-"} (${bno || "-"}) • Apt: ${apt || "-"}`
                      : "";

                  return (
                    <div
                      key={addr.id}
                      className={`flex items-start justify-between p-4 border rounded-lg transition-colors ${
                        addr.isDefault
                          ? "border-[#006C35] bg-green-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => setDefaultAddress(addr.id)}
                          className={`mt-1 ${
                            addr.isDefault
                              ? "text-[#006C35]"
                              : "text-gray-400 hover:text-gray-600"
                          }`}
                          title={addr.isDefault ? "Default Address" : "Set as Default"}
                        >
                          {addr.isDefault ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                        </button>

                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {addr.type || "Other"}
                            </span>
                            {addr.isDefault && (
                              <span className="text-xs bg-[#006C35] text-white px-2 py-0.5 rounded-full">
                                Default
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-gray-700 font-medium mt-1">
                            {addr.addressText || "-"}
                          </p>

                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            {buildingLine ? <p>{buildingLine}</p> : null}
                            {addr.phone ? <p>{addr.phone}</p> : null}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => deleteAddress(addr.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="px-6">
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#006C35] hover:bg-[#005428] px-8"
              disabled={saving || authLoading || !uid || uploadingPhoto}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
